import * as HoverCard from '@radix-ui/react-hover-card'
import { Link } from 'react-router'
import { FindingHoverCard, toFindingSeverityLevel } from './finding-hover-card.tsx'
import * as styles from './linked-finding-chip.css.ts'

export type LinkedFindingChipProps = {
  findingId: string
  // App UI route to navigate to on click (e.g. `/scans/{scanId}`). Callers derive this from the
  // wire's `_links` HAL sub-object — the wire's `href` points at the REST API path, not a UI
  // route, so building this at the callsite keeps the chip decoupled from that translation.
  // Absent when the backreference could not be resolved; the chip degrades to an unlinked label.
  link?: string
  tool?: string
  rule?: string
  path?: string
  severity?: string
}

/**
 * A finding reference rendered inline in overlay content: a compact chip labelled with the rule
 * and file path, linking into the scan that surfaced the finding and previewing the full context
 * on hover / focus. When the backreference can't be resolved (no `link`), renders an unlinked
 * `findingId` label with no hovercard, matching the wire contract described alongside
 * `FindingOverlayClusterFindingSchema`.
 */
export const LinkedFindingChip: React.FC<LinkedFindingChipProps> = ({
  findingId,
  link,
  tool,
  rule,
  path,
  severity,
}) => {
  if (!link) {
    return <span className={styles.unlinkedChip}>{findingId}</span>
  }

  const level = toFindingSeverityLevel(severity)
  const label = [rule, path].filter(r => !!r).join(' ') || findingId

  return (
    <HoverCard.Root openDelay={120} closeDelay={120}>
      <HoverCard.Trigger asChild>
        <Link to={link} className={styles.chip} aria-label={`View finding ${label} in scan`}>
          {rule && <span className={styles.rule}>{rule}</span>}
          {path && <span className={styles.path}>{path}</span>}
          <span className={styles.severityDot({ level })} aria-hidden="true" />
        </Link>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content side="bottom" align="start" sideOffset={4} className={styles.hoverContent}>
          <FindingHoverCard findingId={findingId} link={link} tool={tool} rule={rule} path={path} severity={severity} />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  )
}
